import React, { useEffect, useRef, forwardRef } from 'react';
import { CardState, CharacterCard as CardData } from '../types';
import Spinner from './Spinner';
import { safeFormatDate } from '../utils/dateFormatting';
import { useMobileDetection } from '../hooks/useMobileDetection';
import InteractiveCard3D from './InteractiveCard3D';
import { navigate } from './Router';



const DeleteIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
    </svg>
);

interface CardProps {
  card: CardData;
  onGenerate: (id: string) => void;
  onDelete: (id: string) => void;
  onCancel?: (id: string) => void; // キャンセル専用のハンドラー（トーストなし）
  isLastCard?: boolean; // 最後のカードかどうか（遅延ロード用）
  onLastCardVisible?: (node: HTMLDivElement | null) => void; // 最後のカードが見えた時のコールバック
}

const Card = forwardRef<HTMLDivElement, CardProps>(({
  card,
  onGenerate,
  onDelete,
  onCancel,
  isLastCard = false,
  onLastCardVisible
}, ref) => {
  const isMobile = useMobileDetection();

  
  const statusMessage = () => {
    switch (card.status) {
        case CardState.LOADING_DESCRIPTION:
            return "Classifying & analyzing image...";
        case CardState.LOADING_IMAGE:
            return "Generating pixel art...";
        case CardState.SAVING:
            return "Saving pixel art...";
        default:
            return "";
    }
  };

  const getKeyword = () => {
    if (!card.characterData || !card.characterData.mainObjects || card.characterData.mainObjects.length === 0) {
      return "PIXEL";
    }
    // 最初の特徴的なオブジェクトを取得
    return card.characterData.mainObjects[0];
  };





  const renderCardContent = () => {
    switch (card.status) {
      case CardState.IDLE:
        return (
            <div className="relative w-full h-full bg-theme-tertiary rounded-lg flex flex-col items-center justify-end pb-6 text-center overflow-hidden">
                <img src={card.originalImageUrl} alt="Original Upload" className="absolute inset-0 w-full h-full object-cover" loading="lazy" decoding="async" />
                <div className="absolute bottom-0 left-0 w-full h-2/5 bg-gradient-to-t from-black via-black/70 to-transparent pointer-events-none"></div>
                <button
                    onClick={(e) => { 
                      e.preventDefault();
                      e.stopPropagation();
                      onGenerate(card.id);
                    }}
                    onTouchStart={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onTouchEnd={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      onGenerate(card.id);
                    }}
                    className={`relative z-10 flex items-center justify-center w-4/5 px-4 py-2 text-base font-medium text-white rounded-md focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-gray-900 transition-all duration-300 hover:scale-105 active:scale-95 touch-manipulation ${isMobile ? 'touch-manipulation' : ''} bg-gradient-to-r from-green-800 via-green-400 to-yellow-400 hover:from-green-700 hover:via-green-300 hover:to-yellow-300 bg-size-200 bg-pos-0 hover:bg-pos-100 animate-gradient-x shadow-[0_0_20px_rgba(34,197,94,0.3)] hover:shadow-[0_0_25px_rgba(168,85,247,0.4)]`}
                >
                    <span className="relative z-10 font-bold tracking-wide">Generate</span>
                    <div className="absolute inset-0 rounded-md bg-gradient-to-r from-green-800/10 via-green-400/10 to-yellow-400/10 animate-pulse"></div>
                </button>
                {/* Delete button - モバイルでは常に表示、デスクトップではhover時表示 */}
                <button
                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onDelete(card.id); }}
                    className={`absolute top-2 right-2 z-20 p-2 text-white bg-black/60 rounded-full hover:bg-black/80 
                               transition-all duration-200 
                               ${isMobile ? 'opacity-100' : 'opacity-0 group-hover:opacity-100 transform translate-x-2 group-hover:translate-x-0'}`}
                    aria-label="Delete this photo"
                >
                    <DeleteIcon />
                </button>
            </div>
        );
      case CardState.LOADING_DESCRIPTION:
      case CardState.LOADING_IMAGE:
        return (
            <div className="w-full h-full bg-theme-secondary rounded-lg flex flex-col items-center justify-center text-theme-primary p-4">
                <Spinner />
                <p className="mt-4 text-sm text-center animate-pulse">{statusMessage()}</p>
                <button
                    onClick={(e) => { 
                      e.preventDefault();
                      e.stopPropagation();
                      if (onCancel) {
                        onCancel(card.id); // キャンセル専用ハンドラー（トーストなし）
                      } else {
                        onDelete(card.id); // フォールバック
                      }
                    }}
                    className="mt-4 text-xs text-theme-secondary hover:text-theme-primary transition-colors font-bold"
                >
                    Cancel
                </button>
            </div>
        );
      case CardState.SAVING:
        return (
            <div className="w-full h-full bg-theme-secondary rounded-lg flex flex-col items-center justify-center text-theme-primary p-4 relative overflow-hidden">
                {/* 生成完了したピクセルアートを薄く表示 */}
                <div className="absolute inset-0 opacity-30">
                  <img
                    src={card.pixelArtUrl || ''}
                    alt="Generated Pixel Art Preview"
                    loading="lazy"
                    decoding="async"
                    className="pixelated w-full h-full object-cover rounded-lg"
                  />
                </div>
                
                {/* 保存中インジケーター */}
                <div className="relative z-10 flex flex-col items-center">
                  <Spinner />
                  <div className="mt-4 text-center text-sm font-medium animate-pulse">
                      {statusMessage()}
                  </div>
                  <div className="mt-2 text-xs text-theme-tertiary">
                      Preparing for display...
                  </div>
                </div>
            </div>
        );
      case CardState.SUCCESS:
        const keyword = getKeyword();
        
        return (
          <InteractiveCard3D 
            className="w-full h-full" 
            isEnabled={!isMobile}
          >
            <a
              href={`/c/${card.id}`}
              onClick={(e) => { e.preventDefault(); navigate(`/c/${card.id}`); }}
              className="relative w-full h-full cursor-pointer block touch-manipulation"
            >
              {/* Front of Card */}
              <div className="absolute w-full h-full bg-theme-tertiary/50 rounded-lg cursor-pointer overflow-hidden animate-light-shine-with-border">
                <img
                  src={card.pixelArtUrl || ''}
                  alt="Generated Pixel Art"
                  loading="lazy"
                  decoding="async"
                  className="pixelated w-full h-full object-cover"
                />
                
                {/* Light Shine Effects - カスタムCSS */}
                <div className="absolute inset-0 pointer-events-none">
                  {/* Left Top Light Beams - 2本の白い帯 */}
                  <div className="light-beam-1"></div>
                  <div className="light-beam-2"></div>
                  
                  {/* Right Bottom Reflection - 反射の1本 */}
                  <div className="light-reflection"></div>
                </div>

                {/* Date and Keyword Labels */}
                <div className="absolute bottom-2 left-2">
                    <div className="fira-sans-book-italic text-white text-xs opacity-90 mb-0 tracking-wider">
                      {safeFormatDate(card.exifData?.createDate || card.createdAt || new Date())}
                    </div>
                    <span className="fira-sans-extra-bold-italic text-white text-xl md:text-2xl capitalize leading-none md:leading-tight">
                      {keyword}
                    </span>
                </div>
              </div>
              {/* Back of Card */}
              <div className="absolute w-full h-full [backface-visibility:hidden] [transform:rotateY(180deg)] rounded-lg cursor-pointer overflow-hidden">
                <img src={card.originalImageUrl} alt="Original Upload" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>
            </a>
          </InteractiveCard3D>
        );
      case CardState.ERROR:
        return (
            <div className="w-full h-full bg-theme-secondary border border-red-500 text-red-300 rounded-lg flex flex-col items-center justify-center p-4 text-center">
                <p className="font-bold">Generation Failed</p>
                <p className="text-sm my-2 text-theme-secondary">{card.error}</p>
                <div className="flex flex-col space-y-2">
                    <button
                        onClick={(e) => { 
                          e.preventDefault();
                          e.stopPropagation();
                          onGenerate(card.id);
                        }}
                        className="px-4 py-1 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-offset-theme-primary focus:ring-red-500"
                    >
                        Retry
                    </button>
                    <button
                        onClick={(e) => { 
                          e.preventDefault();
                          e.stopPropagation();
                          onDelete(card.id);
                        }}
                        className="text-xs text-theme-tertiary hover:text-theme-primary transition-colors font-bold"
                    >
                        Cancel
                    </button>
                </div>
            </div>
        );
      default:
        return null;
    }
  };

  // 遅延ロード用のref設定
  const cardRef = useRef<HTMLDivElement>(null);
  
  useEffect(() => {
    if (isLastCard && onLastCardVisible && cardRef.current) {
      onLastCardVisible(cardRef.current);
    }
  }, [isLastCard, onLastCardVisible]);

  return (
    <div 
      ref={isLastCard ? cardRef : ref}
      className="w-full aspect-[3/4] group light-theme-no-border"
    >
      {renderCardContent()}
    </div>
  );
});

// React.memoでメモ化してレンダリング最適化
const MemoizedCard = React.memo(Card, (prevProps, nextProps) => {
  // カードの主要なプロパティが変更された場合のみ再レンダリング
  return (
    prevProps.card.id === nextProps.card.id &&
    prevProps.card.status === nextProps.card.status &&
    prevProps.card.pixelArtUrl === nextProps.card.pixelArtUrl &&
    prevProps.card.error === nextProps.card.error &&
    prevProps.isLastCard === nextProps.isLastCard
  );
});

MemoizedCard.displayName = 'Card';

export default MemoizedCard;
